<template>
  <!-- 面板模式：直接显示内容 -->
  <div v-if="isPanelMode" class="dataset-editor-panel">
    <div class="editor-header">
      <div class="header-left">
        <i class="el-icon-document"></i>
        <span class="dataset-name">{{ datasetName }}</span>
        <el-tag type="info" size="small" style="margin-left: 12px;">
          共 {{ totalCount }} 条数据
        </el-tag>
        <el-tag type="success" size="small" style="margin-left: 8px;">
          <i class="el-icon-user"></i> 所属: {{ ownerName }}
        </el-tag>
      </div>
      <div class="header-right">
        <el-tag v-if="hasUnsavedChanges" type="warning" size="small">
          <i class="el-icon-warning"></i> 有未保存的更改
        </el-tag>
              <el-button 
                type="primary"
                icon="el-icon-edit" 
                size="small" 
                @click="handleEditRow"
                :disabled="selectedRowIds.length !== 1">
                修改
              </el-button>
        <el-button 
          icon="el-icon-refresh" 
          size="small" 
          @click="loadData"
          :loading="loading">
          刷新
        </el-button>
      </div>
    </div>

    <div v-loading="loading" class="table-container">
      <el-table
        :data="paginatedTableData"
        border
        stripe
        :height="tableHeight"
        :row-class-name="getRowClassName"
        @cell-dblclick="handleCellDoubleClick"
        @row-click="handleRowClick"
        highlight-current-row
        style="width: 100%">
        
              <!-- 选择列 -->
              <el-table-column
                width="50"
                align="center">
                <template slot="header">
                  <el-checkbox 
                    v-model="isAllSelected"
                    @change="handleSelectAll"
                    :indeterminate="isIndeterminate">
                  </el-checkbox>
                </template>
                <template slot-scope="scope">
                  <el-checkbox 
                    v-model="selectedRowIds"
                    :label="scope.row.id"
                    @change="handleRowSelect">
                  </el-checkbox>
                </template>
              </el-table-column>
        
        <!-- 序号列 -->
        <el-table-column
          label="#"
          width="60"
          align="center">
          <template slot-scope="scope">
            {{ (currentPage - 1) * pageSize + scope.$index + 1 }}
          </template>
        </el-table-column>
        
        <!-- 动态列 -->
        <el-table-column
          v-for="(header, index) in headers"
          :key="header.id"
          :prop="`header_${index}`"
          :label="header.attributeName"
          :width="header.width"
          :min-width="120">
          <template slot-scope="scope">
            <div class="cell-content" :class="{ 'cell-updated': isCellUpdated(scope.row.id, index) }">
              <!-- 编辑模式 -->
              <el-input
                v-if="editingCell.rowId === scope.row.id && editingCell.headerIndex === index"
                v-model="editingCell.value"
                size="small"
                ref="cellInput"
                @blur="handleCellBlur"
                @keyup.enter.native="handleCellSave"
                @keyup.esc.native="handleCellCancel">
              </el-input>
              <!-- 查看模式 -->
              <span v-else class="cell-text-wrapper">
                <span class="cell-text">
                  {{ scope.row[`header_${index}`] || '-' }}
                </span>
                <!-- 被修改标记 -->
                <el-tooltip 
                  v-if="isCellUpdated(scope.row.id, index)" 
                  placement="top"
                  :content="`旧值: ${getCellChangeInfo(scope.row.id, index).oldValue || '空'}`">
                  <el-tag 
                    type="warning" 
                    size="mini" 
                    effect="dark"
                    class="cell-update-badge">
                    已更新
                  </el-tag>
                </el-tooltip>
              </span>
            </div>
          </template>
        </el-table-column>
        
        <!-- 状态列 -->
        <el-table-column
          label="状态"
          width="100"
          align="center">
          <template slot-scope="scope">
            <el-tag 
              :type="scope.row.isUsed === 0 ? 'success' : 'info'" 
              size="mini">
              {{ scope.row.isUsed === 0 ? '使用中' : '已废弃' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <!-- 共享状态列 -->
        <el-table-column
          label="共享"
          width="80"
          align="center">
          <template slot-scope="scope">
            <i 
              :class="scope.row.share ? 'el-icon-check' : 'el-icon-close'" 
              :style="{ color: scope.row.share ? '#67C23A' : '#909399' }">
            </i>
          </template>
        </el-table-column>
        
        <!-- 敏感数据列 -->
        <el-table-column
          label="敏感"
          width="80"
          align="center">
          <template slot-scope="scope">
            <i 
              :class="scope.row.hasSensitive ? 'el-icon-warning' : 'el-icon-success'" 
              :style="{ color: scope.row.hasSensitive ? '#F56C6C' : '#909399' }">
            </i>
          </template>
        </el-table-column>
        
        <!-- 版本列 -->
        <el-table-column
          label="版本"
          width="80"
          align="center"
          prop="version">
        </el-table-column>
      </el-table>
    </div>

    <div class="editor-footer">
      <div class="footer-top">
        <div class="footer-left">
          <el-alert
            v-if="versionCheckMessage"
            :title="versionCheckMessage"
            :type="versionCheckType"
            :closable="false"
            show-icon
            class="version-alert">
          </el-alert>
        </div>
        <div class="footer-right">
          <span class="last-check-time" v-if="lastCheckTime">
            上次检查: {{ lastCheckTime }}
          </span>
        </div>
      </div>
      
      <div class="footer-pagination">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalCount"
          background>
        </el-pagination>
      </div>
    </div>
    
    <!-- 编辑行弹框 -->
    <el-dialog
      title="编辑数据"
      :visible.sync="editRowDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      :before-close="handleCancelRowEdit"
      :append-to-body="!isPanelMode">
      
      <el-form 
        :model="editRowForm" 
        ref="editRowForm" 
        label-width="120px"
        v-loading="editRowLoading">
        <el-form-item 
          v-for="(header, index) in headers" 
          :key="header.id"
          :label="header.attributeName"
          :prop="`header_${index}`">
          <el-input 
            v-model="editRowForm[`header_${index}`]" 
            :placeholder="`请输入${header.attributeName}`"
            :disabled="editRowLoading"
            clearable>
          </el-input>
        </el-form-item>
      </el-form>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="handleCancelRowEdit" size="medium">
          <i class="el-icon-close"></i> 取消
        </el-button>
        <el-button 
          type="primary" 
          @click="handleSaveRowEdit" 
          size="medium"
          :loading="editRowLoading">
          <i class="el-icon-check"></i> 保存
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getDataDetailById, updateSingleDataById, getVersion, lockData } from '@/api'

export default {
  name: 'DatasetTableEditor',
  
  props: {
    visible: {
      type: Boolean,
      default: true
    },
    databaseId: {
      type: [Number, String, null],
      default: null
    },
    datasetName: {
      type: String,
      default: '未命名数据集'
    },
    userOperatorId: {
      type: String,
      required: true
    },
    isPanelMode: {
      type: Boolean,
      default: true  // 默认使用面板模式
    }
  },
  
  data() {
    return {
      loading: false,
      headers: [],
      tableData: [],
      isTxt: 0,
      hasUnsavedChanges: false,
      ownerName: '未知',  // 数据集拥有者名称
      ownerId: '',         // 数据集拥有者ID
      
      // 分页
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      
            // 表格高度
            tableHeight: 500,
            
            // 行选择
            selectedRowIds: [], // 改为数组，支持多选
            
            // 行编辑弹框
      editRowDialogVisible: false,
      editRowLoading: false,
      editRowForm: {},
      editRowOriginalData: {},
      
      // 编辑单元格状态
      editingCell: {
        rowId: null,
        headerIndex: null,
        value: '',
        originalValue: ''
      },
      
      // 版本轮询
      versionCheckTimer: null,
      versionCheckInterval: 300000, // 5分钟
      versionCheckMessage: '',
      versionCheckType: 'info',
      lastCheckTime: null,
      updatedRowIds: [], // 存储被更新的行ID
      updatedCells: {}, // 存储被更新的单元格 { 'rowId_headerIndex': { oldValue, newValue } }
      changeDetails: [] // 存储变更详情列表
    }
  },
  
        computed: {
          /**
           * 分页后的表格数据
           */
          paginatedTableData() {
            const start = (this.currentPage - 1) * this.pageSize
            const end = start + this.pageSize
            return this.tableData.slice(start, end)
          },
          
          /**
           * 当前页所有行的ID
           */
          currentPageRowIds() {
            return this.paginatedTableData.map(row => row.id)
          },
          
          /**
           * 是否全选
           */
          isAllSelected: {
            get() {
              if (this.currentPageRowIds.length === 0) return false
              return this.currentPageRowIds.every(id => this.selectedRowIds.includes(id))
            },
            set(val) {
              // setter 由 handleSelectAll 处理
            }
          },
          
          /**
           * 是否半选（部分选中）
           */
          isIndeterminate() {
            const selectedCount = this.currentPageRowIds.filter(id => this.selectedRowIds.includes(id)).length
            return selectedCount > 0 && selectedCount < this.currentPageRowIds.length
          },
          
          /**
           * 已选择的行对象
           */
          selectedRows() {
            return this.tableData.filter(row => this.selectedRowIds.includes(row.id))
          }
        },
  
  watch: {
    visible(val) {
      if (!this.isPanelMode) {
        if (val) {
          this.loadData()
          this.startVersionCheck()
          this.$nextTick(() => {
            this.calculateTableHeight()
          })
        } else {
          this.stopVersionCheck()
          this.resetEditingState()
        }
      }
    },
    
    databaseId(val) {
      // 面板模式下，当 databaseId 变化时重新加载数据
      if (this.isPanelMode && val) {
        this.loadData()
        this.startVersionCheck()
        this.$nextTick(() => {
          this.calculateTableHeight()
        })
      }
    }
  },
  
  mounted() {
    window.addEventListener('resize', this.calculateTableHeight)
    
    // 面板模式下，mounted 时自动加载数据
    if (this.isPanelMode && this.databaseId) {
      this.loadData()
      this.startVersionCheck()
      this.$nextTick(() => {
        this.calculateTableHeight()
      })
    }
  },
  
  beforeDestroy() {
    this.stopVersionCheck()
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  
  methods: {
    /**
     * 计算表格高度
     */
    calculateTableHeight() {
      // 计算可用高度：视口高度 * 0.94 - 对话框头部 - 对话框底部 - 编辑器头部 - 编辑器底部
      const viewportHeight = window.innerHeight
      const dialogHeight = viewportHeight * 0.94
      // 对话框头部约55px，对话框底部约50px，编辑器头部约50px，编辑器底部约100px (包含分页)
      const usedHeight = 55 + 50 + 50 + 100
      this.tableHeight = Math.max(300, dialogHeight - usedHeight)
    },
    
    /**
     * 加载数据
     */
    async loadData() {
      // 如果 databaseId 为空，不执行加载
      if (!this.databaseId) {
        return
      }
      
      try {
        this.loading = true
        const response = await getDataDetailById({
          database_id: this.databaseId,
          user_id: this.userOperatorId
        })
        
        if (response.code === 200 && response.data) {
          this.headers = response.data.header || []
          this.isTxt = response.data.isTxt || 0
          this.ownerName = response.data.ownerName || '未知'
          this.ownerId = response.data.ownerId || ''
          
          // 转换数据格式
          this.tableData = (response.data.value || []).map(item => {
            const row = {
              id: item.id,
              isUsed: item.isUsed,
              share: item.share,
              hasSensitive: item.hasSensitive,
              version: item.version
            }
            
            // 将 header_0, header_1 等字段映射到行数据
            this.headers.forEach((header, index) => {
              row[`header_${index}`] = item[`header_${index}`] || ''
            })
            
            return row
          })
          
          // 更新总数和重置页码
          this.totalCount = this.tableData.length
          this.currentPage = 1
          
          // 清空被更新行标记（用户已刷新查看最新数据）
          this.updatedRowIds = []
          this.updatedCells = {}
          this.changeDetails = []
          this.versionCheckMessage = ''
          
          this.$message.success('数据加载成功')
        }
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error(error.message || '加载数据失败')
      } finally {
        this.loading = false
      }
    },
    
          /**
           * 行点击事件
           */
          handleRowClick(row) {
            // 点击行时切换选中状态
            const index = this.selectedRowIds.indexOf(row.id)
            if (index > -1) {
              this.selectedRowIds.splice(index, 1)
            } else {
              this.selectedRowIds.push(row.id)
            }
          },
          
          /**
           * 行选择事件
           */
          handleRowSelect() {
            // checkbox 自动处理选中状态，这里不需要额外处理
          },
          
          /**
           * 全选/取消全选
           */
          handleSelectAll(val) {
            if (val) {
              // 全选当前页
              this.currentPageRowIds.forEach(id => {
                if (!this.selectedRowIds.includes(id)) {
                  this.selectedRowIds.push(id)
                }
              })
            } else {
              // 取消选中当前页
              this.selectedRowIds = this.selectedRowIds.filter(
                id => !this.currentPageRowIds.includes(id)
              )
            }
          },
    
    /**
     * 打开行编辑弹框
     */
    async handleEditRow() {
      if (this.selectedRowIds.length === 0) {
        this.$message.warning('请先选择要编辑的数据')
        return
      }
      
      if (this.selectedRowIds.length > 1) {
        this.$message.warning('只能选择一行数据进行编辑')
        return
      }
      
      // 获取选中的行
      const selectedRow = this.tableData.find(row => row.id === this.selectedRowIds[0])
      if (!selectedRow) {
        this.$message.error('找不到选中的数据')
        return
      }
      
      try {
        // 尝试锁定数据
        const lockResponse = await lockData({
          value_id: selectedRow.id,
          lock_user_id: this.userOperatorId
        })
        
        if (lockResponse.code === 0 && lockResponse.data && lockResponse.data.lock) {
          // 锁定成功，准备编辑表单数据
          const formData = {}
          const originalData = {}
          
          this.headers.forEach((header, index) => {
            const key = `header_${index}`
            formData[key] = selectedRow[key] || ''
            originalData[key] = selectedRow[key] || ''
          })
          
          // 使用 $set 确保响应式
          this.editRowForm = { ...formData }
          this.editRowOriginalData = { ...originalData }
          
          // 等待 DOM 更新后再打开对话框
          this.$nextTick(() => {
            this.editRowDialogVisible = true
          })
        } else {
          // 锁定失败，数据已被其他用户锁定
          const lockMessage = lockResponse.data?.lockMessage || '该数据正在被其他用户编辑'
          this.$message.error(lockMessage)
        }
      } catch (error) {
        console.error('锁定数据失败:', error)
        this.$message.error(error.message || '锁定数据失败')
      }
    },
    
    /**
     * 保存行编辑
     */
    async handleSaveRowEdit() {
      if (this.selectedRowIds.length === 0) {
        return
      }
      
      const selectedRow = this.tableData.find(row => row.id === this.selectedRowIds[0])
      if (!selectedRow) {
        return
      }
      
      try {
        this.editRowLoading = true
        
        // 调用更新接口
        const response = await updateSingleDataById({
          value_id: selectedRow.id,
          user_operator_id: this.userOperatorId,
          excel_value: this.editRowForm
        })
        
        if (response.code === 0) {
          // 更新本地数据
          this.headers.forEach((header, index) => {
            const key = `header_${index}`
            selectedRow[key] = this.editRowForm[key]
          })
          selectedRow.version += 1
          
          this.$message.success('保存成功')
          this.editRowDialogVisible = false
        }
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error(error.message || '保存失败')
      } finally {
        this.editRowLoading = false
      }
    },
    
    /**
     * 取消行编辑
     */
    async handleCancelRowEdit() {
      // 检查是否有更改
      let hasChanges = false
      for (const key in this.editRowForm) {
        if (this.editRowForm[key] !== this.editRowOriginalData[key]) {
          hasChanges = true
          break
        }
      }
      
      if (hasChanges) {
        try {
          await this.$confirm('有未保存的更改，确定要取消吗？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '继续编辑',
            type: 'warning'
          })
        } catch {
          return
        }
      }
      
      // 注意：取消编辑时，锁会在一定时间后自动释放，不需要手动解锁
      
      this.editRowDialogVisible = false
      // 使用 $nextTick 确保对话框完全关闭后再清空数据
      this.$nextTick(() => {
        this.editRowForm = {}
        this.editRowOriginalData = {}
      })
    },
    
    /**
     * 双击单元格开始编辑
     */
    async handleCellDoubleClick(row, column, cell, event) {
      // 检查是否为可编辑列（排除序号、状态等列）
      if (!column.property || !column.property.startsWith('header_')) {
        return
      }
      
      const headerIndex = parseInt(column.property.replace('header_', ''))
      
      // 尝试锁定数据
      try {
        const lockResponse = await lockData({
          value_id: row.id,
          lock_user_id: this.userOperatorId
        })
        
        if (lockResponse.code === 0 && lockResponse.data && lockResponse.data.lock) {
          // 锁定成功，进入编辑模式
          this.editingCell = {
            rowId: row.id,
            headerIndex: headerIndex,
            value: row[column.property] || '',
            originalValue: row[column.property] || ''
          }
          
          // 等待 DOM 更新后聚焦输入框
          this.$nextTick(() => {
            const input = this.$refs.cellInput
            if (input && input[0]) {
              input[0].focus()
            }
          })
        } else {
          // 锁定失败，数据已被其他用户锁定
          const lockMessage = lockResponse.data?.lockMessage || '该数据正在被其他用户编辑'
          this.$message.error(lockMessage)
        }
      } catch (error) {
        console.error('锁定数据失败:', error)
        this.$message.error(error.message || '锁定数据失败')
      }
    },
    
    /**
     * 单元格失去焦点
     */
    handleCellBlur() {
      // 延迟执行，让保存操作有机会执行
      setTimeout(() => {
        if (this.editingCell.rowId !== null) {
          this.handleCellCancel()
        }
      }, 200)
    },
    
    /**
     * 保存单元格
     */
    async handleCellSave() {
      if (this.editingCell.rowId === null) {
        return
      }
      
      // 检查值是否改变
      if (this.editingCell.value === this.editingCell.originalValue) {
        this.handleCellCancel()
        return
      }
      
      try {
        // 构建更新数据
        const row = this.tableData.find(r => r.id === this.editingCell.rowId)
        const excelValue = {}
        
        this.headers.forEach((header, index) => {
          if (index === this.editingCell.headerIndex) {
            excelValue[`header_${index}`] = this.editingCell.value
          } else {
            excelValue[`header_${index}`] = row[`header_${index}`] || ''
          }
        })
        
        // 调用更新接口
        const response = await updateSingleDataById({
          value_id: this.editingCell.rowId,
          user_operator_id: this.userOperatorId,
          excel_value: excelValue
        })
        
        if (response.code === 0) {
          // 更新本地数据
          row[`header_${this.editingCell.headerIndex}`] = this.editingCell.value
          row.version += 1
          
          this.$message.success('保存成功')
          this.resetEditingState()
        }
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error(error.message || '保存失败')
      }
    },
    
    /**
     * 取消编辑
     */
    async handleCellCancel() {
      // 注意：取消编辑时，锁会在一定时间后自动释放，不需要手动解锁
      this.resetEditingState()
    },
    
    /**
     * 重置编辑状态
     */
    resetEditingState() {
      this.editingCell = {
        rowId: null,
        headerIndex: null,
        value: '',
        originalValue: ''
      }
    },
    
    /**
     * 获取行样式
     */
    getRowClassName({ row }) {
      // 优先标记被更新的行
      if (this.updatedRowIds.includes(row.id)) {
        return 'row-updated'
      }
      if (row.isUsed === 1) {
        return 'row-deprecated'
      }
      return ''
    },
    
    /**
     * 判断单元格是否被修改
     */
    isCellUpdated(rowId, headerIndex) {
      const cellKey = `${rowId}_${headerIndex}`
      return !!this.updatedCells[cellKey]
    },
    
    /**
     * 获取单元格变更信息
     */
    getCellChangeInfo(rowId, headerIndex) {
      const cellKey = `${rowId}_${headerIndex}`
      return this.updatedCells[cellKey] || null
    },
    
    /**
     * 开始版本轮询
     */
    startVersionCheck() {
      this.stopVersionCheck()
      
      // 立即执行一次
      this.checkVersion()
      
      // 设置定时器
      this.versionCheckTimer = setInterval(() => {
        this.checkVersion()
      }, this.versionCheckInterval)
    },
    
    /**
     * 停止版本轮询
     */
    stopVersionCheck() {
      if (this.versionCheckTimer) {
        clearInterval(this.versionCheckTimer)
        this.versionCheckTimer = null
      }
    },
    
    /**
     * 检查版本
     */
    async checkVersion() {
      if (this.tableData.length === 0) {
        return
      }
      
      try {
        const shareValuesVersion = this.tableData.map(row => ({
          valueId: row.id,
          version: row.version
        }))
        
        const response = await getVersion({
          share_values_version: shareValuesVersion
        })
        
        if (response.code === 0 && response.data) {
          const { isNew, rowIds } = response.data
          this.lastCheckTime = new Date().toLocaleTimeString()
          
          if (isNew) {
            this.versionCheckMessage = '所有数据均为最新版本'
            this.versionCheckType = 'success'
            this.updatedRowIds = [] // 清空已更新行标记
            this.updatedCells = {}
            this.changeDetails = []
          } else {
            // 保存被更新的行ID
            this.updatedRowIds = rowIds || []
            
            // 获取最新数据并对比变化
            await this.fetchAndCompareChanges(rowIds)
          }
        }
      } catch (error) {
        console.error('版本检查失败:', error)
      }
    },
    
    /**
     * 获取最新数据并对比变化
     */
    async fetchAndCompareChanges(updatedRowIds) {
      try {
        // 获取最新数据
        const response = await getDataDetailById({
          database_id: this.databaseId,
          user_id: this.userOperatorId
        })
        
        if (response.code === 200 && response.data) {
          const latestData = response.data.value || []
          const changeDetails = []
          const updatedCells = {}
          const updatedRowNumbers = []
          
          // 对比每个被更新的行
          updatedRowIds.forEach(rowId => {
            // 找到本地旧数据
            const oldRow = this.tableData.find(row => row.id === rowId)
            // 找到最新数据
            const newRow = latestData.find(row => row.id === rowId)
            
            if (oldRow && newRow) {
              const rowIndex = this.tableData.findIndex(row => row.id === rowId)
              const rowNumber = rowIndex + 1
              updatedRowNumbers.push(rowNumber)
              
              const rowChanges = []
              
              // 对比每一列
              this.headers.forEach((header, index) => {
                const key = `header_${index}`
                const oldValue = oldRow[key] || ''
                const newValue = newRow[key] || ''
                
                if (oldValue !== newValue) {
                  // 记录变更
                  const cellKey = `${rowId}_${index}`
                  updatedCells[cellKey] = { oldValue, newValue }
                  
                  rowChanges.push({
                    column: header.attributeName,
                    oldValue,
                    newValue
                  })
                }
              })
              
              if (rowChanges.length > 0) {
                changeDetails.push({
                  rowNumber,
                  rowId,
                  changes: rowChanges
                })
              }
            }
          })
          
          // 保存变更信息
          this.updatedCells = updatedCells
          this.changeDetails = changeDetails
          
          // 构建详细提示消息
          let detailMessage = ''
          changeDetails.forEach(detail => {
            const columnNames = detail.changes.map(c => c.column).join('、')
            detailMessage += `第 ${detail.rowNumber} 行的 ${columnNames} 字段已更新\n`
          })
          
          // 构建简短提示
          const rowNumbersText = updatedRowNumbers.length > 0 
            ? `第 ${updatedRowNumbers.join(', ')} 行` 
            : `${updatedRowIds.length} 条`
          
          this.versionCheckMessage = `检测到 ${rowNumbersText}数据有更新，建议刷新！`
          this.versionCheckType = 'warning'
          
          // 显示详细通知
          this.$notify({
            title: '数据更新提醒',
            dangerouslyUseHTMLString: true,
            message: `<div style="max-height: 300px; overflow-y: auto;">
                        <p><strong>${rowNumbersText}数据已被其他用户更新：</strong></p>
                        <div style="font-size: 12px; line-height: 1.8; margin-top: 8px;">
                          ${detailMessage.split('\n').filter(line => line).map(line => 
                            `<div style="margin: 4px 0;">• ${line}</div>`
                          ).join('')}
                        </div>
                        <p style="margin-top: 12px; color: #E6A23C;">建议刷新查看最新内容</p>
                      </div>`,
            type: 'warning',
            duration: 0, // 不自动关闭
            showClose: true
          })
        }
      } catch (error) {
        console.error('获取最新数据失败:', error)
        // 即使获取失败，也显示基本提示
        this.$notify({
          title: '数据更新提醒',
          message: `有 ${updatedRowIds.length} 条数据已被其他用户更新，建议刷新查看最新内容`,
          type: 'warning',
          duration: 0,
          showClose: true
        })
      }
    },
    
    /**
     * 每页显示条数改变
     */
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
    },
    
    /**
     * 当前页改变
     */
    handleCurrentChange(val) {
      this.currentPage = val
    },
    
    /**
     * 关闭对话框
     */
    handleClose() {
      if (this.editingCell.rowId !== null) {
        this.$confirm('有正在编辑的单元格，确定要关闭吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.handleCellCancel()
          this.resetSelection()
          if (this.isPanelMode) {
            this.$emit('close')
          } else {
            this.$emit('update:visible', false)
          }
        }).catch(() => {})
      } else {
        this.resetSelection()
        if (this.isPanelMode) {
          this.$emit('close')
        } else {
          this.$emit('update:visible', false)
        }
      }
    },
    
          /**
           * 重置选择状态
           */
          resetSelection() {
            this.selectedRowIds = []
          }
  }
}
</script>

<style scoped>
/* 面板模式样式 */
.dataset-editor-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow: hidden;
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif;
}

.dataset-editor-dialog {
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #EBEEF5;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-left i {
  font-size: 20px;
  color: #409EFF;
}

.dataset-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.table-container {
  margin: 0;
  padding: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.table-container >>> .el-table {
  flex: 1;
}

/* 复选框样式 */
.table-container >>> .el-checkbox {
  margin: 0;
}

.table-container >>> .el-checkbox__label {
  display: none;
}

.table-container >>> .el-checkbox__input {
  margin: 0;
}

.cell-content {
  min-height: 24px;
  display: flex;
  align-items: center;
  position: relative;
}

/* 被更新的单元格样式 */
.cell-content.cell-updated {
  background-color: #FEF0E0;
  border-left: 2px solid #E6A23C;
  padding-left: 6px;
}

.cell-text-wrapper {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.cell-text {
  flex: 1;
  padding: 4px 0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cell-text:hover {
  background-color: #F5F7FA;
  padding: 4px 8px;
  margin: 0 -8px;
  border-radius: 4px;
}

/* 单元格更新标记 */
.cell-update-badge {
  flex-shrink: 0;
  font-size: 10px;
  padding: 0 4px;
  height: 18px;
  line-height: 18px;
  margin-left: 4px;
  animation: badge-pulse 2s ease-in-out infinite;
}

@keyframes badge-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.editor-footer {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #EBEEF5;
  flex-shrink: 0;
}

.footer-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.footer-left {
  flex: 1;
}

.version-alert {
  margin: 0;
}

.footer-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.last-check-time {
  font-size: 12px;
  color: #909399;
}

.footer-pagination {
  display: flex;
  justify-content: center;
  padding: 4px 0;
}

/* 已废弃行的样式 */
.dataset-editor-dialog >>> .row-deprecated {
  background-color: #F5F5F5 !important;
  opacity: 0.6;
}

.dataset-editor-dialog >>> .row-deprecated:hover > td {
  background-color: #FAFAFA !important;
}

/* 被更新行的样式 - 橙色高亮 */
.dataset-editor-dialog >>> .row-updated {
  background-color: #FDF6EC !important;
  animation: highlight-fade 2s ease-in-out;
}

.dataset-editor-dialog >>> .row-updated:hover > td {
  background-color: #FEF0E0 !important;
}

.dataset-editor-dialog >>> .row-updated > td {
  border-left: 3px solid #E6A23C !important;
}

/* 高亮动画效果 */
@keyframes highlight-fade {
  0% {
    background-color: #F9C175;
  }
  50% {
    background-color: #FDF6EC;
  }
  100% {
    background-color: #FDF6EC;
  }
}

/* 对话框样式 */
.dataset-editor-dialog >>> .el-dialog {
  display: flex;
  flex-direction: column;
  height: 94vh;
  margin: 3vh auto !important;
}

.dataset-editor-dialog >>> .el-dialog__body {
  padding: 12px 16px;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.dataset-editor-dialog >>> .el-dialog__header {
  border-bottom: 1px solid #EBEEF5;
  padding: 14px 16px;
  flex-shrink: 0;
}

.dataset-editor-dialog >>> .el-dialog__footer {
  border-top: 1px solid #EBEEF5;
  padding: 10px 16px;
  flex-shrink: 0;
}
</style>

